import React, { useState } from 'react';
import './OrderItemCard.css';
import { serviceProviderAcceptOrRejectOrder } from "../../Apiservices/Auth/servers/profileServer";

const OrderItemCard = ({ orderData }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isAccepted, setIsAccepted] = useState(orderData.is_accepted);
  const [isRejected, setIsRejected] = useState(!(orderData.is_accepted ?? true));

  // ✅ Loading states
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format time
  const formatTime = (timeString) => {
    return timeString || 'Not specified';
  };

  // ✅ Accept Order
  const handleAccept = async () => {
    setAcceptLoading(true);

    try {
      const error = await serviceProviderAcceptOrRejectOrder(
        orderData.order_item_id,
        true
      );

      if (error) {
        alert(error);
        return;
      }

      setIsAccepted(true);
      setIsRejected(false);
    } catch (e) {
      alert("Failed to accept order");
    } finally {
      setAcceptLoading(false);
    }
  };

  // Open reject modal
  const handleReject = () => {
    setShowRejectModal(true);
  };

  // ✅ Confirm Reject
  const confirmReject = async () => {
    setRejectLoading(true);

    try {
      const error = await serviceProviderAcceptOrRejectOrder(
        orderData.order_item_id,
        false
      );

      if (error) {
        alert(error);
        return;
      }

      setIsRejected(true);
      setIsAccepted(false);
      setShowRejectModal(false);
    } catch (e) {
      alert("Failed to reject order");
    } finally {
      setRejectLoading(false);
    }
  };

  const cancelReject = () => {
    setShowRejectModal(false);
  };

  // Status badge
  const getStatusBadge = () => {
    if (isAccepted) return 'badge-accepted';
    if (isRejected) return 'badge-rejected';
    return 'badge-pending';
  };

  const getStatusText = () => {
    if (isAccepted) return 'Accepted';
    if (isRejected) return 'Rejected';
    return 'Pending';
  };

  return (
    <>
      <div className="order-card">
        {/* Header */}
        <div className="order-header">
          <div className="order-title-section">
            <h2 className="order-title">
              {orderData.event?.title || 'Event'}
            </h2>
            <span className={`status-badge ${getStatusBadge()}`}>
              {getStatusText()}
            </span>
          </div>
          <div className="order-id">
            Order ID: #{orderData.order_item_id}
          </div>
        </div>

        {/* User Info */}
        <div className="user-info">
          <div className="info-item">
            <span className="info-label">Customer:</span>
            <span className="info-value">
              {orderData.user?.name || 'N/A'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Mobile:</span>
            <span className="info-value">
              {orderData.user?.mobile || 'N/A'}
            </span>
          </div>
        </div>

        {/* Event Details */}
        <div className="event-details">
          <h3 className="section-title">Event Details</h3>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Date:</span>
              <span className="detail-value">
                {formatDate(orderData.event?.date)}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Time:</span>
              <span className="detail-value">
                {formatTime(orderData.event?.time)}
              </span>
            </div>
            <div className="detail-item full-width">
              <span className="detail-label">Venue:</span>
              <span className="detail-value">
                {orderData.event?.venue || 'Not specified'}
              </span>
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="service-details">
          <h3 className="section-title">Service Details</h3>
          <div className="service-info">
            <span className="service-name">
              {orderData.service?.name || 'Service'}
            </span>
            {orderData.isPackage && orderData.packageDetails && (
              <span className="package-badge">Package</span>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="items-section">
          <h3 className="section-title">Items</h3>
          <div className="items-table">
            <div className="table-header">
              <div className="col-item">Item</div>
              <div className="col-people">People</div>
              <div className="col-price">Unit Price</div>
              <div className="col-total">Total</div>
            </div>

            {orderData.items?.map((item, index) => (
              <div key={index} className="table-row">
                <div className="col-item">
                  {item.item_name || `Item #${item.item_id}`}
                </div>
                <div className="col-people">
                  {item.number_of_people}
                </div>
                <div className="col-price">
                  ₹{item.regular_price}
                </div>
                <div className="col-total">
                  ₹{item.total_amount}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div className="pricing-summary">
          <div className="summary-row">
            <span>Total:</span>
            <span>₹{orderData.pricing?.total_amount || 0}</span>
          </div>
          <div className="summary-row">
            <span>Paid:</span>
            <span>₹{orderData.pricing?.paid_amount || 0}</span>
          </div>
          <div className="summary-row">
            <span>Balance:</span>
            <span>₹{orderData.pricing?.balance_to_be_paid || 0}</span>
          </div>
        </div>

        {/* Buttons */}
        {!isAccepted && !isRejected && (
          <div className="action-buttons">
            <button
              className="btn-accept"
              onClick={handleAccept}
              disabled={acceptLoading || rejectLoading}
            >
              {acceptLoading ? "Accepting..." : "✓ Accept"}
            </button>

            <button
              className="btn-reject"
              onClick={handleReject}
              disabled={acceptLoading || rejectLoading}
            >
              ✗ Reject
            </button>
          </div>
        )}

        {/* Status */}
        {isAccepted && (
          <div className="status-message accepted">
            This order has been accepted
          </div>
        )}

        {isRejected && (
          <div className="status-message rejected">
            This order has been rejected
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Rejection</h3>
            <p>Are you sure you want to reject this order?</p>

            <div className="modal-actions">
              <button onClick={cancelReject}>
                Cancel
              </button>

              <button
                onClick={confirmReject}
                disabled={rejectLoading}
              >
                {rejectLoading ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderItemCard;